/**
 * UniqueId Extension — Assigns a stable UUID (nodeId) to every block-level node.
 *
 * Purpose: The incremental audit diff compares paragraphs by identity (nodeId)
 * instead of text content. This correctly handles duplicates, reordering, and
 * paragraph splits — scenarios where text-based diffing breaks.
 *
 * Behavior:
 * - On creation: every paragraph/heading/blockquote gets a crypto.randomUUID()
 * - On paste: pasted content gets fresh UUIDs (avoids duplicate IDs)
 * - On split: the original paragraph keeps its ID, new one gets a fresh ID
 * - On undo: ProseMirror restores the original node attrs (including nodeId)
 * - On load: existing nodeIds are parsed from data-node-id attributes
 */

import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'

const BLOCK_TYPES = ['paragraph', 'heading', 'blockquote']

const uniqueIdPluginKey = new PluginKey('uniqueId')

export const UniqueId = Extension.create({
  name: 'uniqueId',

  addGlobalAttributes() {
    return [
      {
        types: BLOCK_TYPES,
        attributes: {
          nodeId: {
            default: null,
            parseHTML: (el) => el.getAttribute('data-node-id'),
            renderHTML: (attrs) =>
              attrs.nodeId ? { 'data-node-id': attrs.nodeId } : {},
          },
        },
      },
    ]
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: uniqueIdPluginKey,

        appendTransaction(transactions, _oldState, newState) {
          // Only act when the document actually changed
          if (!transactions.some((tr) => tr.docChanged)) return null

          const { tr } = newState
          let modified = false
          const seen = new Set<string>()

          newState.doc.descendants((node, pos) => {
            if (!BLOCK_TYPES.includes(node.type.name)) return

            const id = node.attrs.nodeId
            if (!id || seen.has(id)) {
              // Missing ID or duplicate — assign a fresh one
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                nodeId: crypto.randomUUID(),
              })
              modified = true
            } else {
              seen.add(id)
            }
          })

          return modified ? tr : null
        },
      }),
    ]
  },
})
